import { AlertTriangle, LogOut, RefreshCw, ShieldOff, UserX, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { AuthError } from "@/contexts/AuthErrorContext";
import { useApolloClient } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { clearTokens } from "@/lib/token-utils";
import { removeAuthCookie } from "@/lib/cookie-utils";
import { isImpersonating } from "@/lib/session-utils";

interface AuthErrorScreenProps {
  error: AuthError;
  onRetry?: () => void;
}

const AuthErrorScreen = ({ error, onRetry }: AuthErrorScreenProps) => {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      clearTokens();
      if (!isImpersonating()) {
        removeAuthCookie();
      }
      // Stop in-flight queries before clearing, and don't refetch (we just
      // dropped the tokens); resetStore here caused unhandled rejections.
      apolloClient.stop();
      await apolloClient.clearStore();
    } catch (e) {
      console.error('Error during logout:', e);
    }
    // Navigate to login
    navigate('/login', { replace: true });
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    // Force reload to retry all queries
    window.location.reload();
  };

  // Get icon and styling based on error type
  const getErrorConfig = () => {
    switch (error.type) {
      case 'tenant_access':
        return {
          icon: ShieldOff,
          iconBg: 'bg-amber-500/10',
          iconColor: 'text-amber-500',
          title: 'Access Denied',
          subtitle: 'Organization access issue',
        };
      case 'user_not_found':
        return {
          icon: UserX,
          iconBg: 'bg-amber-500/10',
          iconColor: 'text-amber-500',
          title: 'Session Issue',
          subtitle: 'Please sign in again',
        };
      case 'session_expired':
        return {
          icon: Clock,
          iconBg: 'bg-blue-500/10',
          iconColor: 'text-blue-500',
          title: 'Session Expired',
          subtitle: 'Please log in again',
        };
      case 'unauthorized':
      default:
        return {
          icon: AlertTriangle,
          iconBg: 'bg-orange-500/10',
          iconColor: 'text-orange-500',
          title: 'Authentication Required',
          subtitle: 'Unable to verify your session',
        };
    }
  };

  const config = getErrorConfig();
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="max-w-md w-full p-8 space-y-6 text-center border-border/50 shadow-xl">
        {/* Logo */}
        <div className="flex justify-center mb-2">
          <img 
            src="/Logo White Transparent.png" 
            alt="FlowLabs" 
            className="h-10 w-auto opacity-80"
          />
        </div>

        {/* Error Icon */}
        <div className="flex justify-center">
          <div className={`w-20 h-20 rounded-full ${config.iconBg} flex items-center justify-center`}>
            <Icon className={`w-10 h-10 ${config.iconColor}`} />
          </div>
        </div>
        
        {/* Error Title & Message */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{config.title}</h1>
          <p className="text-sm text-muted-foreground">{config.subtitle}</p>
        </div>

        {/* Detailed Message */}
        <div className="p-4 bg-muted/50 rounded-lg text-left">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {error.message}
          </p>
        </div>

        {/* Suggestions */}
        <div className="text-left space-y-2">
          <p className="text-sm font-medium">What you can do:</p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>Log out and sign back in with your account</li>
            <li>Contact your organization administrator if the issue persists</li>
            <li>Try refreshing the page if this seems like a temporary issue</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-2">
          <Button 
            onClick={handleLogout} 
            className="w-full" 
            size="lg"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out & Sign In Again
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRetry}
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>

        {/* Support Link */}
        <p className="text-xs text-muted-foreground pt-2">
          If you continue to experience issues, please contact{" "}
          <a 
            href="mailto:punit@flowlabs.live,marcus@flowlabs.live" 
            className="text-primary hover:underline"
          >
            support@flowlabs.live
          </a>
        </p>
      </Card>
    </div>
  );
};

export default AuthErrorScreen;
